import { useTranslator } from '@bluebeam/react-i18n';
import BaseInput from 'components/inputs/BaseInput';
import BaseTextarea from 'components/inputs/BaseTextarea';
import SinglePunchAssignees from 'components/inputs/dropdowns/AssigneesTabsMultiSelect/SinglePunchAssignees';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import OrderedSetMultiSelect from 'components/inputs/dropdowns/OrderedSetMultiSelect';
import Label from 'components/inputs/Label';
import PrioritySelect from 'components/inputs/PrioritySelect';
import StatusSelect from 'components/inputs/StatusSelect';
import { InsertFilesSubmitParams } from 'components/modals/InsertFilesModal/InsertFilesModal';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlayPunchPhotos, {
  PunchPhoto,
} from 'components/Overlays/OverlayPunchPhotos';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import OverlayViewerContainer from 'components/Overlays/OverlayViewerContainer';
import AppStoreContext from 'contexts/AppStoreContext';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import Immutable from 'immutable';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CreatePunch } from 'records/interactions/CreatePunch';
import { UpdatePunch } from 'records/interactions/UpdatePunch';
import { NewMarkup } from 'records/NewMarkup';
import { Punch, PunchStatusValue } from 'records/Punch';
import { UploadData, UploadDataList } from 'records/UploadData';
import {
  addInsertPhotos,
  deleteInsertedPhoto as deleteInsertedPhotoService,
} from 'services/punchService';
import { getDataURL } from 'utils/general';
import * as toast from 'utils/toast';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from '../MapOverlayMachine';

interface Props {
  interaction: CreatePunch;
}

const NewPunchOverlay: FC<Props> = ({ interaction }: Props) => {
  const { translate } = useTranslator([
    'mapView',
    'buttons',
    'toastText',
    'disciplineFilterSection',
    'formLabels',
  ]);
  const { mapOverlayMachineService } = useMapOverlayMachine();
  const location = useLocation();
  const { state, actions } = useContext(AppStoreContext);
  const [punchPhotos, setPunchPhotos] = useState<PunchPhoto[]>([]);
  const savedRef = useRef({ saved: false });
  const [status, setStatus] = useState<PunchStatusValue>(PunchStatusValue.OPEN);
  const { [FeatureFlag.UserGroups]: isUserGroupsEnabled } = useFeatureFlags();

  useEffect(() => {
    // hide PlacePunch Overlay message & footer
    actions.deleteInteractions('PlacePunch');

    return () => {
      if (!savedRef.current.saved) {
        interaction.punch.markups.forEach((m) =>
          actions.removeLocalMarkup(m.intoMarkup())
        );
      }
    };
  }, []);

  const handleClose = (): void => {
    if (location.pathname.includes('map')) {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
      actions.deleteInteractions('PlacePunch');
    } else {
      actions.clearInteractions();
    }
  };

  const handlePunchChange = (
    field: string,
    val: string | number | Immutable.OrderedSet<string>
  ): void => {
    const updatedInteraction = interaction.setIn(['punch', field], val);

    actions.updateInteraction(updatedInteraction);
  };

  const handleStatusChange = (statusVal: PunchStatusValue): void => {
    const { markups } = state;

    const project = state.getProject();
    const nextStatus = project.statuses.find((s) => s.status === statusVal);

    setStatus(statusVal);
    // TODO - after removing editor state, should pageid be passed in here?
    const sheetMarkups = markups.get(
      // editor.state?.itemId ||
      ''
    );

    if (nextStatus && sheetMarkups) {
      const { r, g, b } = nextStatus.statusColor;
      const punchMarkupIDs = interaction.punch.markups.map(
        (m) => m.clientGeneratedID
      );

      sheetMarkups
        .filter((m) => punchMarkupIDs.includes(m.markupid))
        .forEach((m) => {
          try {
            const parsedMarkupJSON = JSON.parse(m.jsonBlob);

            parsedMarkupJSON.fillColor = ['RGB', r, g, b];
            parsedMarkupJSON.strokeColor = ['RGB', r, g, b];

            const s = JSON.stringify(parsedMarkupJSON);

            const n = m.set('jsonBlob', s);
            actions.setLocalMarkup(n);
            const updatedInteraction = interaction
              .updateIn(
                ['punch', 'markups'],
                (
                  punchMarkups: Immutable.List<NewMarkup>
                ): Immutable.List<NewMarkup> =>
                  punchMarkups.map((pm) => pm.set('jsonBlob', s))
              )
              .setIn(['punch', 'statusValue'], statusVal)
              .setIn(['punch', 'status'], statusVal);

            actions.updateInteraction(updatedInteraction);
          } catch (err) {
            console.log(err);
          }
        });
    }
  };

  const handlePhotoUpload = async (files: FileList): Promise<void> => {
    const { selectedProject } = state;

    const filesArr = Array.from(files);

    const uploadDataArr = filesArr.map((photo: File): UploadData => {
      return new UploadData({
        projectid: selectedProject,
        filename: photo.name,
        category: 'imageattachment',
        extension: photo.type,
        filesize: photo.size,
        file: photo,
      });
    });

    const updatedInteraction = interaction.updateIn(
      ['punch', 'photos'],
      (photos: UploadDataList): UploadDataList => photos.concat(uploadDataArr)
    );

    actions.updateInteraction(updatedInteraction);

    const uploadedPunchPhotos = await Promise.all(
      filesArr.map(async (f): Promise<PunchPhoto> => {
        const url = await getDataURL(f);
        return {
          name: f.name,
          url,
        };
      })
    );
    setPunchPhotos((prev) => prev.concat(uploadedPunchPhotos));
  };

  const handlePhotoInsert = async (params: InsertFilesSubmitParams) => {
    const { selectedProject } = state;

    const { success, photos: insertedPhotos } = await addInsertPhotos(
      selectedProject,
      params
    );

    if (!success) {
      toast.error(
        translate('toastText:fileUploadError.message'),
        translate('toastText:fileUploadError.title')
      );
    }

    const updatedInteraction = interaction.updateIn(
      ['punch', 'insertedphotos'],
      (photos: Immutable.List<string>): Immutable.List<string> =>
        photos.concat(insertedPhotos.map((photo) => photo.fileid))
    );

    actions.updateInteraction(updatedInteraction);

    const insertedPunchPhotos = insertedPhotos.map((photo) => ({
      id: photo.fileid,
      name: photo.name,
      url: photo.url,
    }));

    // holds both uploaded and inserted photos
    setPunchPhotos((prev) => prev.concat(insertedPunchPhotos));
  };

  const deleteUploadedPhoto = (photoToDelete: PunchPhoto): void => {
    setPunchPhotos((prev) =>
      prev.reduce<PunchPhoto[]>((updated, currentPhoto) => {
        if (currentPhoto.url !== photoToDelete.url) updated.push(currentPhoto);

        return updated;
      }, [])
    );

    const updatedInteraction = interaction.updateIn(
      ['punch', 'photos'],
      (photos: UploadDataList): UploadDataList =>
        photos.filterNot(
          (photosEntry) =>
            photosEntry.filename === photoToDelete.name &&
            photosEntry.filesize === photoToDelete.file?.size // just so we are sure it works even if files have the same name
        )
    );

    actions.updateInteraction(updatedInteraction);
  };

  const deleteInsertedPhoto = async (
    photoToDelete: PunchPhoto
  ): Promise<void> => {
    const { selectedProject } = state;
    const { id: photoIdToDelete } = photoToDelete;

    await deleteInsertedPhotoService(selectedProject, photoIdToDelete || ''); // just to make ESLint happy

    setPunchPhotos((prev) =>
      prev.reduce<PunchPhoto[]>((updated, currentPhoto) => {
        if (currentPhoto.id !== photoIdToDelete) updated.push(currentPhoto);

        return updated;
      }, [])
    );

    const updatedInteraction = interaction.updateIn(
      ['punch', 'insertedphotos'],
      (photos: Immutable.List<string>): Immutable.List<string> =>
        photos.filterNot((photosEntry) => photosEntry === photoIdToDelete)
    );

    actions.updateInteraction(updatedInteraction);
  };

  const handlePhotoDelete = async (
    photoToDelete: PunchPhoto
  ): Promise<void> => {
    if (photoToDelete.id) {
      deleteInsertedPhoto(photoToDelete);
    } else {
      deleteUploadedPhoto(photoToDelete);
    }
  };

  const handleCreatePunch = async (): Promise<void> => {
    const { createPunch, queryPunches } = actions;

    try {
      // Diff the previous punches keys with the next punch keys
      // to determine what punches have been created.
      const prevPunchKeys = state.punches.keySeq().toSet();
      const nextState = await createPunch(interaction.punch);
      const newPunchKeys = nextState.punches
        .keySeq()
        .toSet()
        .subtract(prevPunchKeys);

      savedRef.current.saved = true;

      await queryPunches();

      if (newPunchKeys.size === 0) {
        toast.error(
          translate('toastText:punchCreateError.message'),
          translate('toastText:punchCreateError.title')
        );
      }

      if (newPunchKeys.size > 0) {
        const punch = nextState.punches.get(
          newPunchKeys.toList().get(0) || '',
          new Punch()
        );

        if (!location.pathname.includes('map')) {
          actions.deleteInteractions('CreatePunch');
        } else {
          actions.deleteInteractions('PlacePunch');
          mapOverlayMachineService.send({
            type: MapOverlayMachineEventsEnum.OPEN_UPDATE_PUNCH_OVERLAY,
            data: {
              interaction: new UpdatePunch({
                punch,
                containerStyles: interaction.containerStyles,
                viewerOverlay: true,
              }),
            },
          });
        }
      }
    } catch (err) {
      console.log('Unable to create punch: ', err);
      toast.error(
        translate('toastText:punchCreateError.message'),
        translate('toastText:punchCreateError.title')
      );
    }
  };

  const createDisabled = (): boolean => {
    const { punch } = interaction;

    return punch.location.length === 0;
  };

  const { punch, viewerOverlay } = interaction;
  const { projects, selectedProject, projectGroups } = state;

  const project = projects.get(selectedProject);

  if (!project) {
    return null;
  }

  const OverlayContainer = viewerOverlay
    ? OverlayViewerContainer
    : OverlayStdContainer;

  const disciplinesArray = project.disciplines.toArray();
  const translatedDisciplines = disciplinesArray.map((discipline) => {
    const translatedDiscipline: `${string}:${string}` =
      `disciplineFilterSection:disciplines.${discipline}` as `${string}:${string}`;
    return {
      value: discipline,
      label: translate(translatedDiscipline, {
        defaultValue: discipline,
      }),
    };
  });
  return (
    <OverlayContainer
      id="new-punch-overlay"
      style={interaction.containerStyles}
    >
      <OverlayHeader
        id="new-punch-overlay-header"
        title={translate('mapView:newPunchOverlay.newPunchItem')}
        onCancelClick={handleClose}
      />
      <div className="column justify-between oa grow-1">
        <div className="m4">
          <div className="mb3">
            <Label
              label={translate('mapView:newPunchOverlay.location')}
              required
            >
              <div
                className="light row justify-end mr3"
                style={{ color: '#666666' }}
              >
                {punch.location.length}/140
              </div>
            </Label>
            <BaseInput
              id="new-punch-overlay-location-input"
              className="location-input w100"
              value={punch.location}
              maxLength={140}
              onChange={({
                target: { value },
              }: React.ChangeEvent<HTMLInputElement>): void =>
                handlePunchChange('location', value)
              }
            />
          </div>
          <div className="mb3">
            <Label label={translate('mapView:newPunchOverlay.description')} />
            <BaseTextarea
              id="new-punch-overlay-description-textarea"
              className="description-textarea w100"
              value={punch.description}
              onChange={({
                target: { value },
              }: React.ChangeEvent<HTMLTextAreaElement>): void =>
                handlePunchChange('description', value)
              }
            />
          </div>
          <div className="row mb3">
            <div className="grow-1 w100 mr3">
              <div className="grow-1 w100 mr3">
                <Label label={translate('mapView:newPunchOverlay.priority')} />
                <PrioritySelect
                  id="new-punch-overlay-priority-select"
                  value={punch.priority}
                  options={project.priorities}
                  onChange={(priority: string) =>
                    handlePunchChange('priority', priority)
                  }
                />
              </div>
            </div>
            <div className="grow-1 w100">
              <Label label={translate('mapView:newPunchOverlay.status')} />
              <StatusSelect
                id="new-punch-overlay-status-select"
                value={status}
                onChange={(value: PunchStatusValue): void =>
                  handleStatusChange(value)
                }
                disabled
              />
            </div>
          </div>
          <div className="mb3 pr2 w50 new-punch-duedate">
            <style>
              {`
                    .new-punch-duedate .react-datepicker-wrapper {
                      width: 100%;
                    }

                    .new-punch-duedate .react-datepicker-popper {
                      margin-left: -20px
                    }
                  `}
            </style>
            <Label label={translate('mapView:newPunchOverlay.dueDate')} />
            <DateDropdown
              id="new-punch-overlay-due-date"
              date={punch.duedate > 0 ? punch.duedate : null}
              minDate={new Date()}
              onChange={(date: Date): void => {
                const val = date ? date.getTime() / 1000 : 0;
                handlePunchChange('duedate', val);
              }}
            />
          </div>
          <div className="mb3">
            <Label label={translate('mapView:newPunchOverlay.assignees')} />
            {isUserGroupsEnabled ? (
              <SinglePunchAssignees
                id="new-punch-overlay-assignee-select"
                assigneesValues={punch.assignees}
                groupAssigneesValues={punch.groupAssignees}
                assigneesOptions={project.assignees}
                groupAssigneesOptions={projectGroups}
                editable={Boolean(true)}
                onChange={(
                  updatedAssignees: Immutable.OrderedSet<string>,
                  type: string
                ): void => {
                  handlePunchChange(type, updatedAssignees);
                }}
              />
            ) : (
              <OrderedSetMultiSelect
                id="new-punch-overlay-assignee-select"
                values={punch.assignees}
                options={project.assignees.sort()}
                onChange={(
                  updatedAssignees: Immutable.OrderedSet<string>
                ): void => handlePunchChange('assignees', updatedAssignees)}
              />
            )}
          </div>
          <OverlayPunchPhotos
            id="new-punch-overlay-photos"
            punchPhotos={punchPhotos}
            onPhotoUpload={handlePhotoUpload}
            onPhotoInsert={handlePhotoInsert}
            onPhotoDelete={handlePhotoDelete}
          />
        </div>
      </div>
      <OverlayFooter
        id="new-punch-overlay-footer"
        confirmText={translate('buttons:save')}
        cancelText={translate('buttons:cancel')}
        onCancelClick={handleClose}
        onConfirmClick={handleCreatePunch}
        confirmDisabled={createDisabled()}
      />
    </OverlayContainer>
  );
};

export default NewPunchOverlay;
